//TODO : Refactor after ai
import type { HTMLAttributes, ReactNode } from "react";
import type {
  FilterChip,
  OverviewColumn,
  OverviewGroup,
  OverviewObject,
  OverviewRollup,
  OverviewView,
  SortDirection,
  SortField,
} from "./types";
import { Attribute } from "./Attribute";
import { ObjectCalendar } from "./ObjectCalendar";
import { ObjectGallery } from "./ObjectGallery";
import { ObjectKanban } from "./ObjectKanban";
import { ObjectListCard } from "./ObjectListCard";
import { ObjectMap } from "./ObjectMap";
import { ObjectRow } from "./ObjectRow";
import { ObjectSummary } from "./ObjectSummary";
import { ObjectTable } from "./ObjectTable";
import { OverviewToolbar } from "./OverviewToolbar";
import { SortControl } from "./SortControl";
import { ViewSwitcher } from "./ViewSwitcher";
import { Badge, EmptyState, Filters } from "./primitives";

/** Which descriptor field each view needs to be meaningful. */
const REQUIRES: Record<OverviewView, string | null> = {
  list: null,
  table: null,
  gallery: "image",
  kanban: "group",
  map: "coords",
  calendar: "date",
  summary: null,
  plan: null,
};

const ALL_VIEWS: OverviewView[] = [
  "list", "table", "gallery", "kanban", "map", "calendar", "summary", "plan",
];

/**
 * Views this collection's data can actually fill. A view mode that renders
 * empty is worse than a missing one.
 */
// eslint-disable-next-line react-refresh/only-export-components -- Kit exportiert Hilfsfunktion neben Komponente
export function supportedViews(
  items: OverviewObject[] = [],
  candidates: OverviewView[] = ALL_VIEWS,
  groupBy = "group"
): OverviewView[] {
  return candidates.filter((view) => {
    const key = REQUIRES[view];
    if (!key) return true;
    const field = view === "kanban" ? groupBy : key;
    return items.some((item) => item[field] != null);
  });
}

export interface ObjectOverviewProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  items?: OverviewObject[];
  view?: OverviewView;
  onViewChange?: (view: OverviewView) => void;
  views?: OverviewView[];
  /** Wie die Sammlung zu einer Mengenübersicht gestapelt wird. */
  rollup?: OverviewRollup;
  /**
   * Die Belegungsansicht der Sammlung. Sie kennt die Ressourcen, auf die
   * sich die Datensätze verteilen, und bekommt dafür den gefilterten Bestand.
   */
  plan?: (items: OverviewObject[]) => ReactNode;
  query?: string;
  onQueryChange?: (value: string) => void;
  searchPlaceholder?: string;
  countLabel?: string;
  filters?: FilterChip[];
  onFilterRemove?: (filter: FilterChip) => void;
  onFilterClear?: () => void;
  onFilterAdd?: () => void;
  filterBar?: ReactNode;
  sortFields?: SortField[];
  sort?: string;
  sortDirection?: SortDirection;
  onSortChange?: (field: string) => void;
  onSortDirectionChange?: (direction: SortDirection) => void;
  columns?: OverviewColumn[];
  groups?: OverviewGroup[];
  groupBy?: string;
  tiles?: ReactNode;
  mapNote?: string;
  selectedId?: string;
  month?: number;
  year?: number;
  onMonthChange?: (month: number, year: number) => void;
  dowLabels?: string[];
  onOpen?: (item: OverviewObject) => void;
  onAdd?: (group: string) => void;
  actions?: ReactNode;
  rowAttributes?: (item: OverviewObject) => ReactNode[];
  rowAction?: (item: OverviewObject) => ReactNode;
  empty?: ReactNode;
  emptyTitle?: string;
  emptyHint?: string;
}

/**
 * One collection of objects, six representations, one toolbar.
 *
 * Presentational: filtering, sorting and search state live in the caller, so a
 * URL or a saved view can drive them — pass `items` already filtered and sorted.
 */
export function ObjectOverview({
  items = [],
  view = "list",
  onViewChange,
  views,
  rollup,
  plan,
  query,
  onQueryChange,
  searchPlaceholder,
  countLabel = "Objekte",
  filters,
  onFilterRemove,
  onFilterClear,
  onFilterAdd,
  filterBar,
  sortFields,
  sort,
  sortDirection = "asc",
  onSortChange,
  onSortDirectionChange,
  columns,
  groups,
  groupBy = "group",
  tiles,
  mapNote,
  selectedId,
  month,
  year,
  onMonthChange,
  dowLabels,
  onOpen,
  onAdd,
  actions,
  rowAttributes,
  rowAction,
  empty,
  emptyTitle,
  emptyHint,
  className = "",
  ...rest
}: ObjectOverviewProps) {
  const available = views ?? supportedViews(items, ALL_VIEWS, groupBy);
  const cols = columns ?? [
    { key: "title", header: "Name" },
    { key: "subtitle", header: "Details" },
    { key: "status", header: "Status", sortable: false },
  ];

  const emptyBlock = empty ?? (
    <div className="bui-ov-surface">
      <EmptyState title={emptyTitle ?? `Keine ${countLabel} für diese Filter`}>
        {emptyHint ?? "Entfernen Sie einen Filter oder erweitern Sie die Suche."}
      </EmptyState>
    </div>
  );

  const body =
    items.length === 0 ? (
      emptyBlock
    ) : view === "table" ? (
      <ObjectTable
        items={items}
        columns={cols}
        sort={sort}
        direction={sortDirection}
        onSortChange={onSortChange}
        onOpen={onOpen}
        rowAction={rowAction}
      />
    ) : view === "gallery" ? (
      <ObjectGallery items={items} onOpen={onOpen} />
    ) : view === "kanban" ? (
      <ObjectKanban
        items={items}
        groups={groups}
        groupBy={groupBy}
        onOpen={onOpen}
        onAdd={onAdd}
      />
    ) : view === "map" ? (
      <ObjectMap
        items={items}
        tiles={tiles}
        note={mapNote}
        selectedId={selectedId}
        onOpen={onOpen}
      />
    ) : view === "plan" && plan ? (
      plan(items)
    ) : view === "summary" && rollup ? (
      <ObjectSummary items={items} rollup={rollup} />
    ) : view === "calendar" ? (
      <ObjectCalendar
        items={items}
        month={month}
        year={year}
        onMonthChange={onMonthChange}
        dowLabels={dowLabels}
        onOpen={onOpen}
      />
    ) : (
      <ObjectListCard>
        {items.map((item) => (
          <ObjectRow
            key={item.id}
            title={item.title}
            meta={item.meta ?? [item.identifier, item.subtitle].filter(Boolean).join(" · ")}
            onClick={onOpen ? () => onOpen(item) : undefined}
            attributes={
              rowAttributes
                ? rowAttributes(item)
                : (item.attributes ?? [])
                    /* Drei Angaben passen in eine Zeile — die dritte ist oft
                       die, an der die Arbeit hängt. */
                    .slice(0, 3)
                    .map((a) => (
                      <Attribute key={a.label} label={a.label} value={a.value} width={140} />
                    ))
            }
            status={
              item.status ? (
                <Badge color={item.statusColor} dot>
                  {item.status}
                </Badge>
              ) : undefined
            }
            action={rowAction?.(item)}
          />
        ))}
      </ObjectListCard>
    );

  return (
    <div
      className={className}
      style={{ display: "flex", flexDirection: "column", gap: 16 }}
      {...rest}
    >
      <OverviewToolbar
        query={query}
        onQueryChange={onQueryChange}
        searchPlaceholder={searchPlaceholder}
        count={items.length}
        countLabel={countLabel}
        actions={actions}
        sort={
          sortFields && sortFields.length > 0 ? (
            <SortControl
              fields={sortFields}
              field={sort}
              direction={sortDirection}
              onFieldChange={onSortChange}
              onDirectionChange={onSortDirectionChange}
            />
          ) : undefined
        }
        filters={
          filterBar ??
          (filters && filters.length > 0 ? (
            <Filters
              filters={filters}
              onRemove={onFilterRemove}
              onClear={onFilterClear}
              onAdd={onFilterAdd}
            />
          ) : undefined)
        }
        viewSwitcher={
          available.length > 1 ? (
            <ViewSwitcher views={available} value={view} onChange={onViewChange} />
          ) : undefined
        }
      />
      {body}
    </div>
  );
}
